import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { App, Events, Workouts } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

// Child Components
import { reorderItems, useStateValue } from "../../config/AppContext";
import { Action } from "../../types/StaticTypes";
import { triggerDebuggerAndThrow } from "../../utils/debugging-utils";
// Modals
import { ConfirmModal as Delete } from "../Modals";
import DragDrop from "../Utilities/DragDrop";
import LoadingSpinner from "../Utilities/LoadingSpinner";
import ExerciseSets from "../Workouts/ExerciseSets";
import ExerciseSetsList from "../Workouts/ExerciseSetsList";

const useStyles = makeStyles((theme) => ({
  "edit-session-form": {
    "& h5": {
      padding: 15,
      marginBottom: 20,
      fontSize: theme.spacing(2),
      fontWeight: 500,
      color: "#525252",
      "& button": {
        textTransform: "none",
        width: 135,
        fontSize: 14,
        fontWeight: 500,
        marginLeft: 15,
        color: "var(--text-primary)",
        "&.MuiButton-outlined": {
          borderColor: "var(--text-primary)",
          "&:hover": {
            backgroundColor: "var(--text-primary)",
            color: "#ffffff",
          },
        },
        "&.MuiButton-contained": {
          backgroundColor: "#dc3545",
          color: "#ffffff",
          border: "1px solid #dc3545",
          padding: "5px 15px",
          "&:hover": {
            backgroundColor: "#ffffff",
            color: "#dc3545",
          },
        },
      },
    },
    "& .session-workout-sets": {
      padding: "0 30px",
      "& .MuiContainer-root": { cursor: "pointer" },
      "& .drag": {
        "& .MuiContainer-root": { cursor: "move" },
      },
      "& button": {
        display: "inline-block",
        marginLeft: 15,
        textTransform: "none",
      },
    },
  },
}));

type SessionEditProps = {
  event?: Events.IAgenda;
  handleWorkout: (params: Workouts.IWorkout) => void;
  resetView: (start: Date | string) => void;
};

const SessionEdit = (props: SessionEditProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { Methods } = useStateValue() as { Methods: App.Methods };
  const { event, handleWorkout } = props;
  const [superset, setSuperSet] = React.useState<Workouts.IExerciseSuperSet | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [toggled, setToggled] = React.useState(false);
  const [toggleDelete, setToggleDelete] = React.useState(false);
  const [sessionWorkoutStatic, setSessionWorkoutStatic]: any = React.useState();
  const [sessionWorkout, setSessionWorkout] = React.useState<Workouts.IWorkout | undefined>();

  const templateWorkout = {
    id: "",
    name: "",
    description: "",
    duration: 0,
    is_deleted: false,
    exercise_super_set: [] as Workouts.IExerciseSuperSet[],
  };
  const initialWorkout = sessionWorkout ? sessionWorkout : templateWorkout;

  const [stateWorkout, setStateWorkout] = React.useReducer(workoutReducer, initialWorkout);

  const delSuperSetList = React.useCallback(() => {
    if (!superset || !sessionWorkout) return;
    const superSetIndex = sessionWorkout.exercise_super_set.findIndex(
      (el) => el.id === superset.id
    );
    sessionWorkout.exercise_super_set.splice(superSetIndex, 1);
    setSessionWorkout(sessionWorkout);
    return setSuperSet(null);
  }, [superset, sessionWorkout]);

  React.useEffect(() => {
    if (event?.session?.workout_name && !sessionWorkout) {
      Methods.getEventDetails(event.id).then(({ event }) => {
        const workout = event?.session?.workout;
        if (workout) return setStateWorkout({ type: "init", payload: workout });
      });
    }

    // TODO: figure out if I can delete this block
    // I cannot see how this branch ever will be hit if the events are dashboard events,
    // as they only have a SessionFullThatUsedToBePreview
    const fullSession = event?.session as Events.SessionFull;
    if (fullSession?.workout && !sessionWorkout) {
      triggerDebuggerAndThrow();
      return setStateWorkout({ type: "init", payload: fullSession.workout });
    }
  }, [event, sessionWorkout, Methods]);

  React.useEffect(() => {
    if (!superset) return setToggleDelete(false);
    delSuperSetList();
  }, [delSuperSetList, superset]);
  if (!sessionWorkout) return <LoadingSpinner />;

  function workoutReducer(workout: Workouts.IWorkout, action: Action) {
    switch (action.type) {
      case "init":
        workout = action.payload;
        setSessionWorkoutStatic(workout);
        setSessionWorkout(action.payload);
        return action.payload;
      case "cancel": {
        if (!!sessionWorkoutStatic) {
          return sessionWorkoutStatic;
        } else {
          return workout;
        }
      }
      case "reorder":
        if (!loading) setLoading(true);
        const { supersetId, targetId } = action.payload as {
          supersetId: number;
          targetId: number;
        };
        let workoutClone = { ...workout };
        workoutClone.exercise_super_set = reorderItems(
          supersetId,
          targetId,
          workoutClone.exercise_super_set
        );
        setSessionWorkout(workoutClone);
        if (loading) setLoading(false);
        return workoutClone;
      default:
        throw new Error(`${action.type} is not a valid option`);
    }
  }

  const generateSuperSetList = (
    accum: JSX.Element[],
    superset: Workouts.IExerciseSuperSet,
    supersetIndex: number
  ) => {
    const exerciseList = superset.exercises.map(
      (exercise, exerciseIndex): JSX.Element => (
        <ExerciseSets key={exerciseIndex} exercise={exercise} />
      )
    );
    accum = [
      ...accum,
      toggled ? (
        <DragDrop key={supersetIndex} id={`superset-${supersetIndex}`} dispatch={setStateWorkout}>
          <ExerciseSetsList
            toggled={toggled}
            key={supersetIndex}
            superset={superset}
            supersetIndex={supersetIndex}
            exerciseList={exerciseList}
            handleSuperSet={handleSuperSet}
          />
        </DragDrop>
      ) : (
        <ExerciseSetsList
          key={supersetIndex}
          superset={superset}
          supersetIndex={supersetIndex}
          exerciseList={exerciseList}
          handleSuperSet={handleSuperSet}
        />
      ),
    ];
    return accum;
  };
  const generateSuperSetSection = () =>
    stateWorkout.exercise_super_set.length > 0
      ? stateWorkout.exercise_super_set.reduce(generateSuperSetList, [])
      : t("No exercises added yet");

  const handleSuperSet = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    const { id } = e.currentTarget;
    const targetSuperSet = sessionWorkout.exercise_super_set.find((el) => el.id === id);
    if (targetSuperSet) return setSuperSet(targetSuperSet);
    return setSuperSet(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!event) return;
    const formData = new FormData(e.currentTarget);
    let params = {};
    formData.forEach((value, name) => {
      if (name === "exercise_super_set") return;
      return Object.assign(params, { [name]: value });
    });
    Object.assign(params, {
      id: sessionWorkout.id,
      exercise_super_set: sessionWorkout.exercise_super_set,
      is_deleted: sessionWorkout.is_deleted,
    });
    return handleWorkout(stateWorkout);
  };

  const handleToggleCancel = () => {
    setToggled(!toggled);
    if (toggled) {
      setStateWorkout({
        type: "cancel",
      });
    }
  };

  return (
    <>
      <form id="session-edit" onSubmit={handleSubmit} className={classes["edit-session-form"]}>
        <section style={{ marginTop: 30 }}>
          <Typography variant="h5">
            <Button variant={toggled ? "contained" : "outlined"} onClick={handleToggleCancel}>
              {toggled ? t("Cancel Reorder") : t("Toggle Reorder")}
            </Button>
          </Typography>
          <input
            id="exercise_super_set"
            name="exercise_super_set"
            type="text"
            style={{ display: "none" }}
            defaultValue={JSON.stringify(sessionWorkout.exercise_super_set)}
          />
          <ul className="session-workout-sets">{generateSuperSetSection()}</ul>
        </section>
      </form>
      {/* Modals */}
      <Delete
        show={toggleDelete}
        onSubmit={delSuperSetList}
        onHide={() => setSuperSet(null)}
        message={t("Are you sure you want to remove this exercise?")}
        title={t("Delete")}
      />
    </>
  );
};

export default SessionEdit;
